/**
 * 이상한 방 - 워프 디졸브 베일 (원본 ↔ 변경본 갈아 끼우기)
 *
 * 근거 문서: `이상한방-컨셉시트-v0.1.md` 4.4절(정답 방)
 *
 * `session.ts`에서 갈라져 나왔다. 정답 방은 문으로 걸어가는 곳이 아니라 **같은 시점에서
 * 방만 갈아 끼우는 것**이므로(4.4절) 카메라는 아무도 건드리지 않는다. 그 교체를
 * 화면에서 감추는 0.4초짜리 먹색 디졸브 하나가 이 파일의 전부다.
 *
 * ## 즉시 컷을 쓰지 않는 이유
 *
 * 즉시 컷은 그 자체가 motion transient라, 쿨타임 30초가 막으려던 깜빡비교를
 * 전환 연출이 그대로 열어 준다. 그래서 **화면을 완전히 덮은 순간에만** 방을 바꾼다
 * (`WARP_SWAP_AT`). 여기서 벗어나면 교체 순간이 살짝 비쳐서, 그 한 프레임이 곧
 * "뭐가 바뀌었는지" 알려 준다 - `render/transition.ts`가 지키는 것과 같은 규칙이다.
 *
 * ## 시계를 읽지 않는다
 *
 * 진행은 프레임 델타로만 흐른다(`advance(dt)`). 탭이 숨어 rAF가 멈추면 디졸브도 함께
 * 멈추므로 화면과 상태가 어긋나지 않는다. `Date.now()`를 쓰면 돌아왔을 때 이미 끝나 있다.
 *
 * ## 왜 `render/`도 `ui/`도 아닌가
 *
 * `render/`는 Three.js만 있고 DOM을 한 줄도 만지지 않는다(현재 0건) - 풀스크린 `div` 하나를
 * 거기 두면 그 층의 성격이 무너진다. `ui/`는 사람이 읽고 누르는 계기판인데 이 베일은
 * 읽을 것도 누를 것도 없다(`aria-hidden`, `pointer-events:none`). 무엇보다 이 모듈이 지키는
 * 것은 위젯의 모양이 아니라 **"교체는 완전히 덮인 뒤에"** 라는 공정성 규약이라, 세션 옆에 둔다.
 *
 * 대신 `game`을 하나도 부르지 않는다. 방을 실제로 갈아 끼우는 일은 주입받은 콜백 하나
 * (`showRoom`)가 하고, 이 파일은 그것을 **언제** 부를지만 안다.
 */

import { clamp01 } from '../core/math'
import { INK_HEX } from '../render/scene'
import type { RoomTransition } from '../render/transition'

/**
 * 디졸브 중 방을 갈아 끼우는 시점(0~1). 화면이 완전히 덮인 한가운데다.
 * 여기서 벗어나면 교체 순간이 살짝 비쳐서, 그 한 프레임이 곧 "뭐가 바뀌었는지" 알려 준다.
 */
const WARP_SWAP_AT = 0.5

/**
 * 디졸브 베일의 z-index.
 *
 * 캔버스(z-index 없음) 위, 조작 레이어(`hud.css` 10~12)와 게임 HUD(`gameHud.css` 20) 아래다.
 * HUD까지 덮으면 전환 중 남은 시간·탄환이 사라져 "멈춘 것"으로 보인다 -
 * 가려야 하는 것은 **방**이지 계기판이 아니다.
 */
const VEIL_Z_INDEX = 5

/**
 * 베일 색. **씬 배경(`render/scene.ts`의 `INK_HEX`)에서 만든다.**
 *
 * 이 베일이 덮는 것은 문서가 아니라 **캔버스**이므로 같아야 하는 값은 씬의 배경색이다.
 * 손으로 적어 둔 `#1a1a1a`는 그 값(`0x191512`)과 미묘하게 달랐고, 그 차이는 디졸브가 가장
 * 짙은 순간에 화면이 아주 살짝 밝아지는 것으로만 드러난다 - 아무도 원인을 짚지 못하는
 * 종류의 어긋남이다. 그래서 사본을 두지 않고 파생시킨다.
 */
const VEIL_COLOR = `#${INK_HEX.toString(16).padStart(6, '0')}`

/** 진행 중인 디졸브 하나. 시계가 아니라 프레임 델타로 흐른다(탭 전환에도 어긋나지 않는다). */
type Warp = { elapsed: number; readonly duration: number; readonly toOrigin: boolean; swapped: boolean }

export type WarpVeilOptions = Readonly<{
  /** 베일을 붙일 DOM 컨테이너. 세션의 `mount`와 같은 곳이다. */
  mount: HTMLElement
  /** 디졸브 한 번의 길이(초). `tuning.warpDissolveSeconds`가 그대로 들어온다. */
  dissolveSeconds: number
  /**
   * 방을 실제로 갈아 끼운다. **화면이 100% 먹색인 순간에만** 불린다.
   * `true`면 원본(정답 방), `false`면 변경본이다.
   */
  showRoom: (toOrigin: boolean) => void
}>

export type WarpVeil = Readonly<{
  /**
   * 원본↔변경본 전환을 시작한다.
   *
   * @param instant 디졸브 없이 즉시 교체한다. 리빌의 깜빡 교차 전용이며,
   *                거기서는 motion transient가 버그가 아니라 연출이다.
   */
  start: (toOrigin: boolean, instant: boolean) => void
  /** 한 프레임 진행(초). */
  advance: (dtSeconds: number) => void
  /**
   * **방이 아직 안 바뀐 프레임인가.** 디졸브는 한가운데에서 교체하므로 그 전까지 화면에
   * 서 있는 것은 직전 방이고, 리듀서가 판정에 쓰는 것은 이미 바뀐 상태다. 사격 가드가
   * 이 창을 읽어 그 한 발을 판정에서 빼낸다(`session.ts`의 `shootAt`).
   */
  isBeforeSwap: () => boolean
  /** 진행 중인 디졸브를 접고 베일을 걷는다. **대기 중이던 교체는 실행하지 않는다.** */
  cancel: () => void
  /** 베일을 DOM에서 걷어낸다. */
  dispose: () => void
}>

export function createWarpVeil(options: WarpVeilOptions): WarpVeil {
  const { dissolveSeconds, showRoom } = options

  const veil = document.createElement('div')
  veil.setAttribute('aria-hidden', 'true')
  veil.style.cssText = [
    'position:fixed',
    'inset:0',
    `background:${VEIL_COLOR}`,
    'opacity:0',
    // 클릭을 먹으면 전환 중 탭이 통째로 사라진다. 가리는 것은 시야뿐이어야 한다.
    'pointer-events:none',
    `z-index:${VEIL_Z_INDEX}`,
  ].join(';')
  options.mount.append(veil)

  let warp: Warp | null = null

  function setVeil(opacity: number): void {
    const next = clamp01(opacity).toFixed(3)
    if (veil.style.opacity === next) return
    veil.style.opacity = next
  }

  function cancel(): void {
    warp = null
    setVeil(0)
  }

  return {
    start(toOrigin: boolean, instant: boolean): void {
      const duration = instant ? 0 : dissolveSeconds

      if (duration <= 0) {
        cancel()
        showRoom(toOrigin)
        return
      }

      warp = { elapsed: 0, duration, toOrigin, swapped: false }
    },

    advance(dtSeconds: number): void {
      if (warp === null) return

      warp.elapsed += dtSeconds
      const progress = clamp01(warp.elapsed / warp.duration)

      if (!warp.swapped && progress >= WARP_SWAP_AT) {
        showRoom(warp.toOrigin)
        warp.swapped = true
      }

      // 삼각 곡선 - 한가운데(교체 시점)에서 1, 양 끝에서 0.
      setVeil(1 - Math.abs(progress * 2 - 1))

      if (progress < 1) return

      if (!warp.swapped) showRoom(warp.toOrigin)
      setVeil(0)
      warp = null
    },

    isBeforeSwap: () => warp !== null && !warp.swapped,
    cancel,

    dispose(): void {
      veil.remove()
    },
  }
}

/* --- 전환 이펙트와의 조정 --------------------------------------------------------- */

/**
 * 원본↔변경본 전환을 시작한다 - 디졸브 자체는 베일이 돌리고, 여기서 하는 일은 **전환 이펙트와의 조정** 하나다.
 * 두 세션(`session.ts` · `matchSession.ts`)의 `warpTo` 배선이 같은 이 함수를 지난다.
 *
 * 전환(`render/transition.ts`)이 아직 돌고 있다면 **여기서 접는다.** 수색 진입 0.6초 안에 원래 방 버튼을 누르면 두 개의 교체가
 * 겹친다. 전환의 대기 교체는 `showRoom(false)`이므로, 그대로 두면 디졸브가 원본으로 바꾼 화면을 전환이 뒤늦게 변경본으로
 * 되돌린다 - 상태는 "원래 방에 있다"인데 화면은 찾는 사람의 방인 어긋남이 남고, 그 상태에서는 조준도 판정도 화면과 다른 방을 본다.
 * `finish()`가 대기 교체를 먼저 실행하므로 순서는 "전환의 교체 → 이번 디졸브"가 되어 마지막에 남는 것이 언제나 방금 요청한
 * 방이다. 전환은 이 시점에 이미 제 일을 마쳤다 - 수색 진입의 방 교체는 먹색 아래에서 끝났다.
 *
 * @param instant 디졸브 없이 즉시 교체한다. 리빌의 깜빡 교차 전용이며, 거기서는 motion transient가 버그가 아니라 연출이다.
 */
export function startWarpOver(
  transition: Pick<RoomTransition, 'finish'>,
  veil: Pick<WarpVeil, 'start'>,
  toOrigin: boolean,
  instant: boolean,
): void {
  transition.finish()
  veil.start(toOrigin, instant)
}
